<script setup lang="ts">
import { Pencil, X } from 'lucide-vue-next';

const properties = withDefaults(defineProps<{ editing?: boolean }>(), { editing: false });
defineEmits<{ edit: [] }>();

const criteriaStore = useSearchCriteriaStore();
const locationLabels = useLocationLabels();
const { t } = useI18n();
const fullLocale = useFullLocale();
const { queryLoading } = useSearchResultsQueryState();

// The criteria row: the multidest route measures the room it may take inside it.
const criteriaElement = ref<HTMLElement>();

// t() and location labels cannot be called in a store --> exception to the no-store-reads-in-computed rule.
const summaryAriaLabel = computed(() => {
  const route = criteriaStore.committedIsMultiDestination
    ? criteriaStore.committedItineraryCityCodes
        .map((cityCode) => locationLabels.city(cityCode))
        .join(` ${t('components.common.flightSummary.routeTo')} `)
    : `${locationLabels.city(criteriaStore.committedOriginCode)} ${t('components.common.flightSummary.routeTo')} ${locationLabels.city(criteriaStore.committedDestinationCode)}`;

  const departureFormatted = criteriaStore.committedDepartureDate
    ? formatDateLong(criteriaStore.committedDepartureDate, fullLocale.value)
    : '';
  const dates = criteriaStore.committedReturnDate
    ? t('components.common.flightSummary.dateRange', {
        from: departureFormatted,
        to: formatDateLong(criteriaStore.committedReturnDate, fullLocale.value),
      })
    : departureFormatted;

  const pax = t('components.search.passengersTypes.paxSummary', criteriaStore.committedPaxCount);
  const cabinClass = criteriaStore.committedCriteriaKey.cabinClass;
  const cabin = cabinClass ? t(`components.search.cabinClassSelector.${cabinClass}`) : undefined;
  const action = t(
    properties.editing ? 'components.common.flightSummary.cancel' : 'components.common.flightSummary.edit',
  );

  return `${[route, dates, pax, cabin].filter(Boolean).join(', ')}. ${action}.`;
});
</script>

<template>
  <button
    :aria-label="summaryAriaLabel"
    :aria-disabled="queryLoading || undefined"
    class="flex w-full items-center bg-brand-primary-700 px-4 py-3 md:h-11 md:px-6 lg:px-8"
    type="button"
    @click="!queryLoading && $emit('edit')"
  >
    <span
      class="mx-auto flex w-full max-w-container items-center justify-between gap-3"
      aria-hidden="true"
    >
      <span
        ref="criteriaElement"
        class="flex min-w-0 flex-1 items-center gap-2 md:gap-3"
      >
        <span class="flex items-center gap-2 md:gap-3">
          <CommonFlightSummaryRoute v-if="!criteriaStore.committedIsMultiDestination" />
          <CommonFlightSummaryMultidestRoute
            v-else
            :container="criteriaElement"
          />
        </span>

        <UiCustomSeparatorDot
          color-class="bg-text-inverse"
          size="s"
        />

        <CommonFlightSummaryDate />

        <UiCustomSeparatorDot
          color-class="bg-text-inverse"
          size="s"
        />

        <CommonFlightSummaryPax />

        <template v-if="criteriaStore.committedCriteriaKey.cabinClass">
          <UiCustomSeparatorDot
            color-class="bg-text-inverse"
            size="s"
          />

          <CommonFlightSummaryCabinClass />
        </template>
      </span>

      <span
        :class="[
          'flex items-center text-text-inverse transition-opacity duration-200',
          queryLoading ? 'opacity-50' : 'opacity-100',
        ]"
      >
        <component
          :is="editing ? X : Pencil"
          class="h-4 w-4 md:hidden"
        />
        <span
          class="hidden text-sm font-semibold underline decoration-transparent underline-offset-4 transition-colors duration-200 hover:decoration-white md:inline"
        >
          {{ $t(editing ? 'components.common.flightSummary.cancel' : 'components.common.flightSummary.edit') }}
        </span>
      </span>
    </span>
  </button>
</template>
